import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { NO_COMPANY_SENTINEL, useGhlTradeContacts, } from "../hooks/useGhlTradeContacts";
import styles from "./GhlTradeContactSelect.module.css";
export const GhlTradeContactSelect = ({ value, onChange, subcontractorGroup, tradeCategory, placeholder = "Select...", allowClear = true, allowCompanyChange = false, disabled = false, fallbackLabel, company: controlledCompany, onCompanyChange, }) => {
    const [internalCompany, setInternalCompany] = useState(null);
    const isControlled = controlledCompany !== undefined;
    const effectiveCompany = isControlled ? controlledCompany : internalCompany;
    const { options, allOptions, companyOptions, loading, error } = useGhlTradeContacts(subcontractorGroup, tradeCategory, effectiveCompany);
    // Prefill company when value (tradespersonId) is already set (e.g. existing assignment)
    // Skip when controlled or allowCompanyChange
    useEffect(() => {
        if (isControlled || allowCompanyChange)
            return;
        if (!value || allOptions.length === 0)
            return;
        const contact = allOptions.find((o) => o.id === value);
        if (!contact)
            return;
        const companyForValue = contact.companyName ?? NO_COMPANY_SENTINEL;
        const needsPrefill = internalCompany == null || internalCompany !== companyForValue;
        if (needsPrefill) {
            setInternalCompany(companyForValue);
        }
    }, [value, allOptions, internalCompany, allowCompanyChange, isControlled]);
    useEffect(() => {
        // Do not clear when fallbackLabel is present - it exists precisely because the contact
        // may not appear in the filtered options (e.g. existing assignment, declined contractor).
        if (fallbackLabel)
            return;
        if (effectiveCompany != null &&
            value &&
            !options.some((o) => o.id === value)) {
            onChange("", undefined, { fromCompanyChange: true });
        }
    }, [effectiveCompany, value, options, onChange, fallbackLabel]);
    const handleCompanyChange = (e) => {
        const next = e.target.value === "" ? null : e.target.value;
        if (isControlled) {
            onCompanyChange?.(next);
        }
        else {
            setInternalCompany(next);
        }
        onChange("", undefined, { fromCompanyChange: true });
    };
    const handleTradespersonChange = (e) => {
        const next = e.target.value;
        const selectedOption = options.find((o) => o.id === next);
        onChange(next, selectedOption?.name);
    };
    const valueInOptions = value && options.some((o) => o.id === value);
    const showFallback = value && !valueInOptions && fallbackLabel;
    const displayValue = valueInOptions || showFallback ? value : "";
    const namePlaceholder = !effectiveCompany
        ? "Select company first"
        : !loading && options.length === 0
            ? "No contacts found"
            : placeholder;
    return (_jsxs("div", { className: styles.wrapper, children: [_jsxs("div", { className: styles.fieldGroup, children: [_jsx("label", { className: styles.label, children: "Company" }), _jsxs("select", { className: `${styles.select} ${error ? styles.selectError : ""}`, value: effectiveCompany ?? "", onChange: handleCompanyChange, disabled: disabled || loading, "aria-invalid": !!error, children: [_jsx("option", { value: "", children: loading ? "Loading…" : placeholder }), !loading &&
                                companyOptions.map((opt) => (_jsx("option", { value: opt.value, children: opt.label }, opt.value)))] })] }), _jsxs("div", { className: styles.fieldGroup, children: [_jsx("label", { className: styles.label, children: "Name of Tradesperson" }), _jsxs("select", { className: `${styles.select} ${error ? styles.selectError : ""}`, value: displayValue, onChange: handleTradespersonChange, disabled: disabled || loading || !effectiveCompany, "aria-invalid": !!error, children: [(allowClear || !value) && (_jsx("option", { value: "", children: loading ? "Loading…" : namePlaceholder })), !loading && showFallback && (_jsx("option", { value: value, children: fallbackLabel })), !loading &&
                                options.map((opt) => (_jsx("option", { value: opt.id, children: opt.name }, opt.id)))] })] }), error && _jsx("p", { className: styles.errorText, children: error })] }));
};
